/**
 * Save a user's (possibly customized) target plan as a brand-new, reusable
 * template — so a dietitian who tweaked an assigned plan for one user's
 * preferences can turn it into a template and assign that to other users,
 * instead of rebuilding the customization by hand for each one.
 *
 * Templates and plans are parallel but separate trees (`template_*` vs
 * `target_plan_*` id fields). Confirmed against the microservice-diets
 * backend: modify-template-meals and modify-template-meal-options both echo
 * back the new rows (id, meal_type_id / template_meal_id+title) in their
 * response, so new ids are read straight off each write instead of refetching
 * the template afterward. sequence_order must be sent on every meal row —
 * the backend silently drops any ADD row where it's null before insert
 * (save_template_meals filters on `sequence_order is not None`), which
 * otherwise looks like a successful no-op with zero meals actually created.
 *
 * A plan may hold several meal rows of the same meal type; the plan UI hides
 * them (buildMealDataByPeriod keys by meal type, last wins), so a coach cannot
 * tell. Templates are one meal per type, so those rows are collapsed the same
 * way before anything is written — otherwise two source meals map onto one
 * template meal and their identically titled options collide on insert
 * ("Title 'Option 1' exists for meal ..."), which fails the whole batch.
 */
import {
  fetchTargetPlanDetails,
  modifyTemplates,
  modifyTemplateMeals,
  modifyTemplateMealOptions,
  modifyTemplateMealOptionFoods,
} from '../api/dietApi';
import { unwrapDietPayload as unwrapPayload, pickModifyList } from './dietModifyResponse';
import { formatIntakeTime } from './copyMealOptionToClientPlan';

const countFoodsInMeals = (meals) =>
  (meals || []).reduce(
    (sum, meal) =>
      sum +
      (meal.meal_options || []).reduce(
        (optSum, opt) => optSum + (opt.foods || []).filter((f) => f?.food_id).length,
        0
      ),
    0
  );

/**
 * One meal per meal type, mirroring how the plan screen renders duplicates:
 * the last row of a type wins (so the template matches what the coach sees),
 * but it keeps the first row's position (so a duplicate that was appended late
 * does not reorder the day). Rows with no meal_type_id are dropped — they
 * cannot be mapped to a template meal at all.
 */
const dedupeMealsByType = (meals) => {
  const byType = new Map();
  (meals || []).forEach((meal, position) => {
    const key = meal?.meal_type_id ? String(meal.meal_type_id) : null;
    if (!key) return;
    const seen = byType.get(key);
    byType.set(key, { meal, position: seen ? seen.position : position });
  });
  return [...byType.values()].sort((a, b) => a.position - b.position).map((e) => e.meal);
};

const optionMapKey = (mealId, title) => `${String(mealId)}::${String(title ?? '').trim()}`;

/** Discard a template we created but could not fill, so failures leave no orphan behind. */
async function discardTemplate(eventTypeId, templateId) {
  try {
    const res = await modifyTemplates({
      event_type_id: eventTypeId,
      template: { operation: 'DELETE', id: templateId },
    });
    return !!res?.success;
  } catch {
    return false;
  }
}

/**
 * Backend write errors name raw ids ("Title 'Option 1' exists for meal <uuid>")
 * and are not fit for a coach to read, so they go to the console and the caller
 * gets our own sentence plus the raw text on `detail`.
 */
const fail = ({ phase, message, res, templateId }) => {
  const detail = res?.message || res?.detail || '';
  if (detail) console.warn(`[saveTargetPlanAsTemplate] ${phase} failed: ${detail}`);
  return {
    ok: false,
    phase,
    message,
    ...(detail ? { detail } : {}),
    ...(templateId ? { templateId } : {}),
  };
};

/**
 * @param {Object} params
 * @param {string} params.eventTypeId - dietEventTypeId; same id already used for both plan and template calls in this app
 * @param {string} params.sourcePlanId
 * @param {string} params.clientId - assignee of the source plan, required to fetch it
 * @param {string} params.title - new template name
 * @param {string} [params.description]
 * @param {string} params.securityId - template.security_id (public/private)
 * @param {Object} [params.prefetchedSource] - plan details from fetchTargetPlanDetails (skips fetch)
 * @param {(phase: 'load'|'create'|'meals'|'options'|'foods') => void} [params.onPhase]
 * @returns {Promise<{ ok: boolean, templateId?: string, phase?: string, message?: string, detail?: string }>}
 *   On failure, `templateId` is present only when the half-built template is
 *   worth opening (structure copied, foods missing). Anything less complete is
 *   deleted, so a failed save leaves nothing in the template list.
 */
export async function saveTargetPlanAsTemplate({
  eventTypeId,
  sourcePlanId,
  clientId,
  title,
  description = '',
  securityId,
  prefetchedSource = null,
  onPhase,
}) {
  if (!eventTypeId || !sourcePlanId || !clientId) {
    return { ok: false, message: 'Missing plan or user information.' };
  }
  if (!title?.trim()) {
    return { ok: false, message: 'Template name is required.' };
  }
  if (!securityId) {
    return { ok: false, message: 'Could not resolve template visibility. Please try again.' };
  }

  onPhase?.('load');
  let source = prefetchedSource;
  if (!source) {
    const detailsRes = await fetchTargetPlanDetails(eventTypeId, sourcePlanId, clientId);
    if (!detailsRes.success) {
      return fail({
        phase: 'load',
        message: 'Could not load the source plan. Please try again.',
        res: detailsRes,
      });
    }
    source = unwrapPayload(detailsRes);
  }

  const sourceMeals = Array.isArray(source?.meals) ? source.meals : [];
  const meals = dedupeMealsByType(sourceMeals);
  if (sourceMeals.length && !meals.length) {
    return fail({
      phase: 'load',
      message: 'This plan has no usable meal types, so it cannot be saved as a template.',
    });
  }

  onPhase?.('create');
  const createRes = await modifyTemplates({
    event_type_id: eventTypeId,
    template: {
      operation: 'ADD',
      title: title.trim(),
      description: (description || '').trim(),
      security_id: securityId,
      priority_order: 0,
    },
  });
  if (!createRes.success || !createRes.id) {
    return fail({ phase: 'create', message: 'Could not create the template.', res: createRes });
  }
  const templateId = createRes.id;

  if (!meals.length) {
    return { ok: true, templateId };
  }

  // Deduping leaves gaps in the source numbering (and the surviving row may
  // have been appended late), so number the template's meals 1..N in the order
  // they are sent rather than inheriting the plan's values.
  onPhase?.('meals');
  const mealRows = meals.map((meal, index) => ({
    operation: 'ADD',
    template_id: templateId,
    meal_type_id: meal.meal_type_id,
    sequence_order: index + 1,
  }));
  const mealsRes = await modifyTemplateMeals({ event_type_id: eventTypeId, meals: mealRows });
  if (!mealsRes.success) {
    const discarded = await discardTemplate(eventTypeId, templateId);
    return fail({
      phase: 'meals',
      message: 'Could not copy the meals from this plan into the template.',
      res: mealsRes,
      templateId: discarded ? null : templateId,
    });
  }

  // The response echoes each new meal's id + meal_type_id (one meal per meal
  // type on a template), so match on meal_type_id directly rather than
  // refetching and matching on the display description. If that field ever
  // stops coming back, fall back to request/response order.
  const newMealRows = pickModifyList(mealsRes, 'meals');
  const newMealIdByMealTypeId = new Map();
  newMealRows.forEach((row) => {
    if (row?.meal_type_id && row?.id) newMealIdByMealTypeId.set(String(row.meal_type_id), row.id);
  });
  if (newMealIdByMealTypeId.size === 0 && newMealRows.length === mealRows.length) {
    mealRows.forEach((row, index) => {
      const id = newMealRows[index]?.id;
      if (id) newMealIdByMealTypeId.set(String(row.meal_type_id), id);
    });
  }

  const sourceMealIdToNew = new Map();
  for (const meal of meals) {
    const newId = newMealIdByMealTypeId.get(String(meal.meal_type_id));
    if (newId) sourceMealIdToNew.set(String(meal.id), newId);
  }
  if (sourceMealIdToNew.size === 0) {
    const discarded = await discardTemplate(eventTypeId, templateId);
    return fail({
      phase: 'meals',
      message: 'The meals were created but could not be matched back to the plan. Please try again.',
      templateId: discarded ? null : templateId,
    });
  }

  // The backend keys options per meal and rejects the whole batch on a repeat
  // title, so never send the same (meal, title) twice — titles that differ only
  // in case or padding count as the same row to it.
  const optionRows = [];
  const sentOptionKeys = new Set();
  for (const meal of meals) {
    const newMealId = sourceMealIdToNew.get(String(meal.id));
    if (!newMealId) continue;
    for (const opt of meal.meal_options || []) {
      const dedupeKey = optionMapKey(newMealId, opt.title).toLowerCase();
      if (sentOptionKeys.has(dedupeKey)) continue;
      sentOptionKeys.add(dedupeKey);
      optionRows.push({
        operation: 'ADD',
        template_meal_id: newMealId,
        title: opt.title ?? '',
        description: opt.description ?? '',
      });
    }
  }

  if (!optionRows.length) {
    return { ok: true, templateId };
  }

  onPhase?.('options');
  const optRes = await modifyTemplateMealOptions({
    event_type_id: eventTypeId,
    meal_options: optionRows,
  });
  if (!optRes.success) {
    const discarded = await discardTemplate(eventTypeId, templateId);
    return fail({
      phase: 'options',
      message: 'The meals were copied, but their options could not be. Please try again.',
      res: optRes,
      templateId: discarded ? null : templateId,
    });
  }

  // The response echoes each new option's id + template_meal_id + title, so
  // match on that composite key directly rather than refetching; fall back to
  // request/response order if those fields ever stop coming back.
  const newOptionRows = pickModifyList(optRes, 'meal_options');
  const newOptionIdByKey = new Map();
  newOptionRows.forEach((row) => {
    if (row?.template_meal_id && row?.id) {
      newOptionIdByKey.set(optionMapKey(row.template_meal_id, row.title), row.id);
    }
  });
  if (newOptionIdByKey.size === 0 && newOptionRows.length === optionRows.length) {
    optionRows.forEach((row, index) => {
      const id = newOptionRows[index]?.id;
      if (id) newOptionIdByKey.set(optionMapKey(row.template_meal_id, row.title), id);
    });
  }

  const sourceOptionIdToNew = new Map();
  for (const meal of meals) {
    const newMealId = sourceMealIdToNew.get(String(meal.id));
    if (!newMealId) continue;
    for (const opt of meal.meal_options || []) {
      const newOptId = newOptionIdByKey.get(optionMapKey(newMealId, opt.title));
      if (newOptId) sourceOptionIdToNew.set(String(opt.id), newOptId);
    }
  }

  const sourceFoodCount = countFoodsInMeals(meals);
  if (sourceFoodCount > 0 && sourceOptionIdToNew.size === 0) {
    // Meals and options are in place; only the foods are missing, so the
    // template is worth opening and finishing by hand.
    return fail({
      phase: 'options',
      templateId,
      message: 'The template structure was copied, but its foods could not be linked.',
    });
  }

  // Foods are keyed per option too, and the server rejects a repeat ("Food with
  // key ... exists"). Two source options that collapsed onto one template
  // option would otherwise send the same row twice and fail the whole batch.
  const foodRows = [];
  const sentFoodKeys = new Set();
  for (const meal of meals) {
    for (const opt of meal.meal_options || []) {
      const newOptId = sourceOptionIdToNew.get(String(opt.id));
      if (!newOptId) continue;
      for (const food of opt.foods || []) {
        if (!food?.food_id) continue;
        const intake_time = formatIntakeTime(food.intake_time);
        const foodKey = `${newOptId}::${food.food_id}::${intake_time ?? ''}`;
        if (sentFoodKeys.has(foodKey)) continue;
        sentFoodKeys.add(foodKey);
        foodRows.push({
          operation: 'ADD',
          template_meal_option_id: newOptId,
          food_id: food.food_id,
          quantity: Number(food.quantity ?? 1),
          quantity_type_id: food.quantity_type_id,
          intake_time,
        });
      }
    }
  }

  if (!foodRows.length) {
    if (sourceFoodCount > 0) {
      return fail({
        phase: 'foods',
        templateId,
        message: 'The template structure was copied, but its foods could not be linked.',
      });
    }
    return { ok: true, templateId };
  }

  onPhase?.('foods');
  const foodRes = await modifyTemplateMealOptionFoods({
    event_type_id: eventTypeId,
    foods: foodRows,
  });
  if (!foodRes.success) {
    return fail({
      phase: 'foods',
      templateId,
      message: 'The template structure was copied, but some foods may be missing.',
      res: foodRes,
    });
  }

  return { ok: true, templateId };
}
